// Disable no-re-export rule for entry point files
/* eslint-disable @atlaskit/editor/no-re-export */

import { findParentNodeClosestToPos, findParentNodeOfType } from '@atlaskit/editor-prosemirror/utils';
import { ProseMirrorContentStatistics } from '../utils/prosemirror-content-statistics';
import { MarkdownSerializerState } from './pm-markdown/serializer';

/**
 * Exporting the IDMap type so that future consumers of the transformer can be more type safe
 */

/**
 * If the first node is a paragraph we need to wrap it in a paragraph node for the markdown conversion to work properly
 */
function createTextWrapper(editorView, fragment) {
  var _findParentNodeCloses;
  var schema = editorView.state.schema;
  var $from = editorView.state.selection.$from;
  var parent = (_findParentNodeCloses = findParentNodeClosestToPos($from, function (node) {
    return node.type.name === 'paragraph' || node.type.name === 'heading';
  })) === null || _findParentNodeCloses === void 0 ? void 0 : _findParentNodeCloses.node;
  if (parent) {
    return schema.nodes.doc.createChecked(null, schema.nodes[parent.type.name].createChecked(parent.attrs, fragment));
  } else {
    // If the node is a paragraph we need the parent to be a doc for the conversion to work correctly
    return schema.nodes.doc.createChecked(null, schema.nodes.paragraph.createChecked(null, fragment));
  }
}

/**
 * This finds the closest list item parent to the current selection
 * Once found it will create a new list with the current fragment as its children
 */
function createListItemWrapper(editorView, fragment) {
  var _findParentNodeCloses2;
  var schema = editorView.state.schema;
  var $from = editorView.state.selection.$from;
  var parent = (_findParentNodeCloses2 = findParentNodeClosestToPos($from, function (node) {
    return node.type.name === 'bulletList' || node.type.name === 'orderedList';
  })) === null || _findParentNodeCloses2 === void 0 ? void 0 : _findParentNodeCloses2.node;
  if (parent) {
    return schema.nodes[parent.type.name].createChecked(parent.attrs, fragment);
  }
  return fragment;
}

/**
 * If the first node is a task item we need to wrap it in a task list node for the markdown conversion to work properly
 */
function createTaskItemWrapper(schema, fragment) {
  return schema.nodes.doc.createChecked(null, schema.nodes.taskList.createChecked(null, fragment));
}
function createLayoutColumnWrapper(editorView, fragment) {
  var schema = editorView.state.schema;
  var parentSection = findParentNodeOfType(schema.nodes.layoutSection)(editorView.state.selection);
  if (parentSection) {
    // Needs to be a doc for the serializer to work as it
    // loops through the fragments children to render
    return schema.nodes.doc.createChecked(null, schema.nodes.layoutSection.createChecked(parentSection.node.attrs, fragment));
  }
  return fragment;
}

/**
 * This function handles partial selections and it will fix any incomplete fragments that won't be converted properly
 * If you do a partial selection, it will not select the parent node of certain nodes text, listItems etc so once it hits the serializer
 * the serializer will just fallback to plain text and not convert the node properly.
 *
 * This function checks the first child of the current selection, if its a node that needs a parent it will find the closest parent
 * and create a new fragment that will be converted properly by the serializer
 */
function checkFragment(editorView, schema, fragment) {
  var _fragment$firstChild;
  switch (fragment === null || fragment === void 0 || (_fragment$firstChild = fragment.firstChild) === null || _fragment$firstChild === void 0 || (_fragment$firstChild = _fragment$firstChild.type) === null || _fragment$firstChild === void 0 ? void 0 : _fragment$firstChild.name) {
    case 'text':
      return createTextWrapper(editorView, fragment);
    case 'listItem':
      return createListItemWrapper(editorView, fragment);
    case 'taskItem':
      return createTaskItemWrapper(schema, fragment);
    case 'layoutColumn':
      return createLayoutColumnWrapper(editorView, fragment);
    default:
      return fragment;
  }
}

/**
 * Converts a selection of nodes from a Prosemirror editor into Markdown.
 *
 * @param editorView - The EditorView instance of the current Prosemirror editor.
 * @param fragment - The ProseMirror node or fragment that is to be converted into Markdown.
 * @param featureToggles - Optional feature toggles to use for the conversion.
 * @param mentionMap - Optional mention map for serializing mentions.
 * @returns An object containing the converted markdown, the fallback state from the Markdown serializer, and the id map from the Markdown serializer.
 */
export function convertProsemirrorToMarkdown(_ref) {
  var editorView = _ref.editorView,
    fragment = _ref.fragment,
    _ref$featureToggles = _ref.featureToggles,
    featureToggles = _ref$featureToggles === void 0 ? {} : _ref$featureToggles,
    _ref$mentionMap = _ref.mentionMap,
    mentionMap = _ref$mentionMap === void 0 ? {} : _ref$mentionMap,
    _ref$idMap = _ref.idMap,
    idMap = _ref$idMap === void 0 ? {} : _ref$idMap;
  fragment = checkFragment(editorView, editorView.state.schema, fragment);
  return convertProsemirrorNodeToMarkdown({
    node: fragment,
    featureToggles: featureToggles,
    mentionMap: mentionMap,
    idMap: idMap
  });
}

/**
 * Converts a ProseMirror node or document node to markdown/ markdown plus.
 *
 * This function uses the `convertProsemirrorToMarkdown` implementation. Using a node instead of a
 * fragment means that we don't need access to the editorView or the schema to get the result.
 *
 * @param node - The ProseMirror node or document node that is to be converted into Markdown.
 * @param featureToggles - Optional feature toggles to use for the conversion.
 * @param mentionMap - Optional mention map for serializing mentions.
 * @returns An object containing the converted markdown, the fallback state from the Markdown serializer, and the id map from the Markdown serializer.
 */
export function convertProsemirrorNodeToMarkdown(_ref2) {
  var node = _ref2.node,
    _ref2$featureToggles = _ref2.featureToggles,
    featureToggles = _ref2$featureToggles === void 0 ? {} : _ref2$featureToggles,
    _ref2$mentionMap = _ref2.mentionMap,
    mentionMap = _ref2$mentionMap === void 0 ? {} : _ref2$mentionMap,
    _ref2$idMap = _ref2.idMap,
    idMap = _ref2$idMap === void 0 ? {} : _ref2$idMap;
  var markdownSerializerState = new MarkdownSerializerState(featureToggles, mentionMap, idMap);
  markdownSerializerState.renderContent(node);
  var pmStats = new ProseMirrorContentStatistics(node);
  var contentStatistics = pmStats.collectStatistics();
  var markdown = markdownSerializerState.out === "\u200C" ? '' :
  // Return empty string if editor only contains a zero-non-width character
  markdownSerializerState.out;
  return {
    markdown: markdown,
    fallbackState: markdownSerializerState.fallbackState,
    idMap: markdownSerializerState.idMap,
    contentStatistics: contentStatistics
  };
}